import React, { useMemo, useState } from "react";
import {
  Activity,
  BrainCircuit,
  CalendarDays,
  ChevronLeft,
  GitCompareArrows,
  HeartPulse,
  History,
  UserRound,
  Waves,
} from "lucide-react";
import {
  computeArchetype,
  computeDimensions,
  computeTodaysFocus,
  loadProfilesLocal,
} from "@/components/assessment/assessmentScoring";
import { getEvidenceMapping } from "@/components/assessment/evidenceMapping";
import { buildImplementationPlan } from "@/components/assessment/implementationPlan";
import { sexLabel } from "@/components/assessment/participantIdentity";
import { PREFERRED_ACTIVITY_OPTIONS } from "@/components/assessment/assessmentData";
import { AppCard, AppPageHeader } from "@/components/daily/CompanionAppUi";

const score = (value) => (Number.isFinite(value) ? value.toFixed(1) : "—");
const signed = (value) => {
  if (!Number.isFinite(value)) return "—";
  if (Math.abs(value) < 0.005) return "No change";
  return `${value > 0 ? "+" : ""}${value.toFixed(2)}`;
};
const activityLabel = (key) =>
  PREFERRED_ACTIVITY_OPTIONS.find((option) => option.key === key)?.label || key;
const withoutLead = (value, lead) =>
  String(value || "").replace(new RegExp(`^${lead}:\\s*`, "i"), "");

const ResponseList = ({ title, items = [] }) => (
  <div className="rounded-2xl p-4 forr-surface-tint">
    <div className="text-[10px] uppercase tracking-[0.16em] forr-text-teal-deep">
      {title}
    </div>
    <ul className="mt-3 space-y-2">
      {items.map((item) => (
        <li key={item} className="text-sm leading-relaxed forr-text-soft">
          — {item}
        </li>
      ))}
    </ul>
  </div>
);

const AssessmentHistory = ({ history, selectedId, onSelect, currentDimensions }) => {
  if (!history.length) return null;
  const selectedIndex = history.findIndex((record) => record.id === selectedId);
  const previous = selectedIndex > 0 ? history[selectedIndex - 1] : null;
  const previousDimensions = previous
    ? previous.dimensions || computeDimensions(previous.answers || {})
    : null;

  return (
    <AppCard className="mt-4" data-testid="assessment-history-panel">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
            style={{ backgroundColor: "var(--forr-teal-tint)", color: "var(--forr-teal-deep)" }}
          >
            <History size={21} />
          </div>
          <div>
            <div className="forr-overline">Assessment history</div>
            <h2 className="mt-1 font-display text-2xl forr-text-ink">
              {history.length} saved assessment{history.length === 1 ? "" : "s"}
            </h2>
            <p className="mt-2 text-sm leading-relaxed forr-text-soft">
              Retaking adds a new baseline record. Earlier assessments stay available for comparison and are never replaced by a later result.
            </p>
          </div>
        </div>
        <label className="text-xs forr-text-mute sm:min-w-[250px]">
          View assessment
          <select
            value={selectedId}
            onChange={(event) => onSelect(event.target.value)}
            className="mt-2 block w-full rounded-xl border border-black/10 bg-white px-3 py-2.5 text-sm forr-text-ink"
          >
            {history
              .slice()
              .reverse()
              .map((record, reverseIndex) => (
                <option key={record.id} value={record.id}>
                  {new Date(record.completedAt).toLocaleDateString()} ·{" "}
                  {record.profileTitle || computeArchetype(record.answers || {}).title}
                  {reverseIndex === 0 ? " · Latest" : ""}
                </option>
              ))}
          </select>
        </label>
      </div>

      {previousDimensions && (
        <div className="mt-5 rounded-2xl p-4 forr-panel-information">
          <div className="flex items-center gap-2 forr-overline">
            <GitCompareArrows size={15} /> Change from the previous assessment
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            {[
              ["State", currentDimensions.state - previousDimensions.state],
              ["Inputs", currentDimensions.inputs - previousDimensions.inputs],
              ["Capacity", currentDimensions.capacity - previousDimensions.capacity],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl bg-white/70 p-3 text-center">
                <div className="font-display text-xl forr-text-ink">{signed(value)}</div>
                <div className="mt-1 text-[9px] uppercase tracking-[0.13em] forr-text-mute">
                  {label}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs leading-relaxed forr-text-mute">
            These differences compare assessment responses. Daily current-state changes remain in Check in and Progress.
          </p>
        </div>
      )}
    </AppCard>
  );
};

const CompanionAssessmentSummary = ({ assessment, onBack, onRetake }) => {
  const history = useMemo(() => loadProfilesLocal(), []);
  const initialRecord = useMemo(
  () =>
    history.find(
      (record) =>
        Number(record.completedAt) === Number(assessment.completedAt),
    ) || history[history.length - 1] || null,
  [assessment.completedAt, history],
);
const [selectedId, setSelectedId] = useState(initialRecord?.id || "");
const selectedRecord = useMemo(
  () => history.find((record) => record.id === selectedId) || initialRecord,
  [history, initialRecord, selectedId],
);
const selectedAssessment = useMemo(
  () =>
    selectedRecord
      ? {
          id: selectedRecord.id,
          answers: selectedRecord.answers,
          identity: selectedRecord.identity || assessment.identity,
          profile: computeArchetype(selectedRecord.answers || {}),
          completedAt: selectedRecord.completedAt,
        }
      : assessment,
  [assessment, selectedRecord],
);

const dimensions = computeDimensions(selectedAssessment.answers);
  const focus = computeTodaysFocus(selectedAssessment.answers);
  const identity = selectedAssessment.identity || {};
  const mapping = useMemo(
    () => getEvidenceMapping(selectedAssessment.profile.key),
    [selectedAssessment.profile.key],
  );
  const plan = useMemo(
    () =>
      buildImplementationPlan({
        profile: selectedAssessment.profile,
        mapping,
        answers: selectedAssessment.answers,
      }),
    [selectedAssessment, mapping],
  );
  const selectedActivities = (
    selectedAssessment.answers.schedule?.preferredActivities || []
  )
    .filter((key) => key && key !== "none")
    .map(activityLabel);
  const scheduled = selectedAssessment.answers.schedule?.scheduledActivities || [];
  const nervousSystemName = mapping?.stateName || selectedAssessment.profile.title;
  const nervousSystemDescription =
    mapping?.stateDescription || selectedAssessment.profile.body;

  return (
    <div data-testid="companion-assessment-summary">
      <AppPageHeader
        eyebrow="Saved assessment"
        title={`${identity.preferredName || "Your"} Adaptive Profile`}
        body="Revisit every baseline, compare changes, and review the nervous-system interpretation, bottom line, immediate response, and expected adaptation."
        onBack={onBack}
      />

      <AppCard tone="action">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white">
                <UserRound size={21} className="forr-text-teal-deep" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] forr-text-mute">
                  Profile owner
                </div>
                <h2 className="mt-1 font-display text-2xl forr-text-ink">
                  {identity.fullName || identity.preferredName || "Participant"}
                </h2>
              </div>
            </div>
            <p className="mt-4 text-sm forr-text-soft">
              Age {identity.age || "—"} · {sexLabel(identity.sex)} · Completed{" "}
              {new Date(selectedAssessment.completedAt).toLocaleDateString()}
            </p>
          </div>
          <button
            type="button"
            onClick={onRetake}
            className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm forr-btn-secondary"
          >
            Retake and add new assessment
          </button>
        </div>
      </AppCard>

      <AssessmentHistory
        history={history}
        selectedId={selectedId}
        onSelect={setSelectedId}
        currentDimensions={dimensions}
      />

      <AppCard tone="action" className="mt-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="forr-overline">Where your nervous system is</div>
            <h2 className="mt-2 font-display text-3xl forr-text-ink">
              {nervousSystemName}
            </h2>
          </div>
          <BrainCircuit size={24} className="shrink-0 forr-text-teal-deep" />
        </div>
        <p className="mt-4 text-sm leading-relaxed forr-text-soft sm:text-base">
          {nervousSystemDescription}
        </p>
      </AppCard>

      <AppCard className="mt-4">
        <div className="forr-overline">Adaptive Profile</div>
        <h2 className="mt-2 font-display text-3xl forr-text-ink">
          {selectedAssessment.profile.title}
        </h2>
        <p className="mt-4 text-sm leading-relaxed forr-text-soft sm:text-base">
          {selectedAssessment.profile.body}
        </p>
        <div className="mt-5 rounded-2xl p-4 forr-surface-tint">
          <div className="text-[10px] uppercase tracking-[0.18em] forr-text-teal-deep">
            Baseline focus
          </div>
          <p className="mt-2 text-sm leading-relaxed forr-text-ink">{focus.text}</p>
        </div>
      </AppCard>

      <div className="mt-4 grid grid-cols-3 gap-3">
        {[
          ["Current State", dimensions.state],
          ["Current Inputs", dimensions.inputs],
          ["Physical Capacity", dimensions.capacity],
        ].map(([label, value]) => (
          <AppCard key={label} className="text-center">
            <div className="font-display text-3xl forr-text-ink">{score(value)}</div>
            <div className="mt-2 text-[9px] uppercase tracking-[0.14em] forr-text-mute">
              {label}
            </div>
          </AppCard>
        ))}
      </div>

      <AppCard tone="information" className="mt-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="forr-overline">What your responses suggest</div>
            <h2 className="mt-2 font-display text-2xl forr-text-ink">
              {nervousSystemName}
            </h2>
          </div>
          <Waves size={23} className="shrink-0 forr-text-teal-deep" />
        </div>
        <p className="mt-4 text-sm leading-relaxed forr-text-soft sm:text-base">
          {mapping?.interpretation || nervousSystemDescription}
        </p>

        <div className="mt-5 grid gap-3 lg:grid-cols-3">
          <ResponseList
            title="What your nervous system is prioritizing"
            items={mapping?.priorities}
          />
          <ResponseList
            title={mapping?.behaviouralTitle || "Possible behavioural outputs"}
            items={mapping?.behaviouralOutputs}
          />
          <ResponseList
            title="Likely recovery challenges"
            items={mapping?.recoveryChallenges}
          />
        </div>

        {mapping?.whyThisMatters && (
          <div className="mt-4 rounded-2xl bg-white/70 p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] forr-text-teal-deep">
              Why this matters
            </div>
            <p className="mt-2 text-sm leading-relaxed forr-text-ink">
              {mapping.whyThisMatters}
            </p>
          </div>
        )}

        {mapping?.signs?.length > 0 && (
          <div className="mt-4">
            <div className="forr-overline">How this may show up</div>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {mapping.signs.map((sign) => (
                <li key={sign} className="text-sm leading-relaxed forr-text-soft">
                  — {sign}
                </li>
              ))}
            </ul>
          </div>
        )}
      </AppCard>

      {plan?.bottomLine && (
        <AppCard tone="action" className="mt-4">
          <div className="forr-overline">The Bottom Line</div>
          <h2 className="mt-2 font-display text-2xl forr-text-ink">
            What is happening—and what to do next.
          </h2>
          <div className="mt-5 space-y-4 text-sm leading-relaxed forr-text-ink sm:text-base">
            <p>{plan.bottomLine.happening}</p>
            <p>{plan.bottomLine.shameRelease}</p>
            <div className="rounded-2xl bg-white/70 p-4">
              <div className="text-[10px] uppercase tracking-[0.16em] forr-text-teal-deep">
                Why this plan helps
              </div>
              <p className="mt-2">{plan.bottomLine.whyPlanHelps}</p>
            </div>
            <div className="rounded-2xl p-4 forr-surface-tint">
              <div className="text-[10px] uppercase tracking-[0.16em] forr-text-teal-deep">
                Your overall suggestion
              </div>
              <p className="mt-2 font-medium">{plan.bottomLine.overallSuggestion}</p>
            </div>
          </div>
        </AppCard>
      )}

      <AppCard className="mt-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="forr-overline">
              Central and autonomic nervous-system interpretation
            </div>
            <h2 className="mt-2 font-display text-2xl forr-text-ink">
              The physiology behind the assessment read
            </h2>
          </div>
          <HeartPulse size={23} className="shrink-0 forr-text-teal-deep" />
        </div>
        <p className="mt-4 text-sm leading-relaxed forr-text-soft">
          {mapping?.professional?.cns}
        </p>
        <p className="mt-4 text-sm leading-relaxed forr-text-ink">
          {mapping?.professional?.interpretation}
        </p>
      </AppCard>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <AppCard tone="information">
          <div className="forr-overline">Expected acute response</div>
          <p className="mt-3 text-sm leading-relaxed forr-text-ink">
            {withoutLead(mapping?.professional?.acute, "Expected acute response")}
          </p>
        </AppCard>
        <AppCard tone="information">
          <div className="forr-overline">Expected repeated adaptation</div>
          <p className="mt-3 text-sm leading-relaxed forr-text-ink">
            {withoutLead(
              mapping?.professional?.repeated,
              "Expected repeated adaptation",
            )}
          </p>
        </AppCard>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <AppCard tone="information">
          <div className="flex items-center gap-3">
            <Activity size={20} className="forr-text-teal-deep" />
            <div>
              <div className="forr-overline">Selected activities</div>
              <h2 className="mt-1 font-display text-xl forr-text-ink">
                What you said may fit
              </h2>
            </div>
          </div>
          {selectedActivities.length ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedActivities.map((label) => (
                <span
                  key={label}
                  className="rounded-full px-3 py-1.5 text-xs forr-text-teal-deep"
                  style={{ backgroundColor: "var(--forr-teal-tint)" }}
                >
                  {label}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm forr-text-soft">
              No preferred activity was selected.
            </p>
          )}
        </AppCard>

        <AppCard tone="information">
          <div className="flex items-center gap-3">
            <CalendarDays size={20} className="forr-text-teal-deep" />
            <div>
              <div className="forr-overline">Scheduled activity</div>
              <h2 className="mt-1 font-display text-xl forr-text-ink">
                Existing commitments
              </h2>
            </div>
          </div>
          {scheduled.length ? (
            <div className="mt-4 space-y-3">
              {scheduled.map((item) => (
                <div key={item.id} className="rounded-2xl p-3 forr-surface-tint">
                  <div className="font-medium forr-text-ink">
                    {activityLabel(item.activity)}
                  </div>
                  <div className="mt-1 text-xs forr-text-mute">
                    {(item.days || []).join(", ")} · {item.time || "Time not set"} ·{" "}
                    {item.duration || "60"} min
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm forr-text-soft">
              No regular scheduled activity was recorded.
            </p>
          )}
        </AppCard>
      </div>

      <button
        type="button"
        onClick={onBack}
        className="mt-6 inline-flex items-center gap-2 text-sm font-medium forr-text-teal-deep"
      >
        <ChevronLeft size={16} /> Back to profile
      </button>
    </div>
  );
};

export default CompanionAssessmentSummary;
